import { CatalogImageOwner } from "../catalog/catalogQueries";
import {
  PrimitiveSymbol3DModelKey,
  getPrimitiveSymbol3DModelKey,
} from "../symbols/registry/primitiveSymbol3DConfig";

export type ProductProxyResolution = {
  renderMode: "primitiveProxy" | "billboardPreview";
  proxyModelKey?: PrimitiveSymbol3DModelKey;
  fallbackReason?: string;
};

const MODEL_KEYWORDS: Array<{
  match: string[];
  models: PrimitiveSymbol3DModelKey[];
}> = [
  { match: ["sofa", "couch"], models: ["living:sofa-3-seat", "living:sofa-2-seat"] },
  { match: ["chair", "armchair", "lounge"], models: ["living:armchair", "dining:chair"] },
  { match: ["table", "coffee"], models: ["living:coffee-table-oval", "dining:round-table"] },
  { match: ["screen", "tv", "television"], models: ["living:screen-wide", "office:screen-wide"] },
  { match: ["bed"], models: ["bedroom:bed-king", "bedroom:bed-single"] },
  { match: ["cabinet"], models: ["kitchen:cabinet"] },
  {
    match: ["closet", "wardrobe", "garderob"],
    models: ["bedroom:closet"],
  },
  {
    match: ["dishwasher", "dish washer"],
    models: ["kitchen:dishwasher"],
  },
  { match: ["fridge", "refrigerator"], models: ["kitchen:fridge-f"] },
  { match: ["sink"], models: ["kitchen:sink-single"] },
  { match: ["stove", "hob", "oven"], models: ["kitchen:stove"] },
];

export function resolveProductProxyForSymbol(
  product: CatalogImageOwner,
  symbolKey: string | undefined,
): ProductProxyResolution {
  const currentModel = getPrimitiveSymbol3DModelKey(symbolKey);
  if (!currentModel) {
    return {
      renderMode: "billboardPreview",
      fallbackReason: "No primitive 3D proxy exists for this symbol.",
    };
  }

  const productText = getProductSearchText(product);
  const matched = MODEL_KEYWORDS.find((candidate) =>
    candidate.match.some((keyword) => productText.includes(keyword)),
  );
  if (!matched) {
    return {
      renderMode: "billboardPreview",
      fallbackReason: "Catalog category does not match a known primitive proxy.",
    };
  }

  if (matched.models.includes(currentModel)) {
    return {
      renderMode: "primitiveProxy",
      proxyModelKey: currentModel,
    };
  }

  return {
    renderMode: "billboardPreview",
    fallbackReason: "Catalog product category differs from the selected symbol family.",
  };
}

export function getSearchTextForPrimitiveSymbol(symbolKey: string | undefined) {
  const modelKey = getPrimitiveSymbol3DModelKey(symbolKey);
  if (!modelKey) return "";
  if (modelKey.includes("sofa")) return "sofa";
  if (modelKey.includes("chair")) return "chair";
  if (modelKey.includes("table")) return "table";
  if (modelKey.includes("screen")) return "screen";
  if (modelKey.includes("bed")) return "bed";
  if (modelKey.includes("cabinet")) return "cabinet";
  if (modelKey.includes("closet")) return "closet";
  if (modelKey.includes("dishwasher")) return "dishwasher";
  if (modelKey.includes("fridge")) return "fridge";
  if (modelKey.includes("sink")) return "sink";
  if (modelKey.includes("stove")) return "stove";
  return modelKey.split(":").pop()?.replace(/-/g, " ") ?? "";
}

function getProductSearchText(product: CatalogImageOwner) {
  const categories = product.categoriesMeta ?? product.categories ?? [];
  const featureValues = Object.values(product.features3 ?? {}).flat();
  return [
    product.name,
    product.description,
    product.brand,
    product.supplier?.name,
    ...categories.map((category) => category?.name),
    ...featureValues.map((value) =>
      Array.isArray(value) ? value.join(" ") : String(value ?? ""),
    ),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}
